const firstNonEmpty = (...values) => {
  for (const value of values) {
    const normalized = String(value ?? '').trim();
    if (normalized) return normalized;
  }
  return '';
};

const normalizeSipHeaderValue = (value) => {
  if (Array.isArray(value)) {
    return firstNonEmpty(...value.map(normalizeSipHeaderValue));
  }

  if (value && typeof value === 'object') {
    return firstNonEmpty(value.raw, value.value, value._raw);
  }

  return firstNonEmpty(value);
};

const ASTERISK_UNIQUE_ID_HEADERS = [
  'X-Web-Call-ID',
  'X-Web-Call-Id',
  'X-WebCallID',
  'X-Asterisk-Uniqueid',
  'X-Asterisk-UniqueID',
  'X-Asterisk-Linkedid',
  'X-Asterisk-LinkedID',
  'X-Uniqueid',
  'X-UniqueID',
  'X-Unique-ID',
  'X-Call-Uniqueid',
  'X-Call-UniqueID',
  'X-Call-ID',
  'X-LinkedID',
  'X-Linked-Id'
];

const ASTERISK_CONTROL_CHANNEL_HEADERS = [
  'X-Original-Channel',
  'X-Asterisk-Channel',
  'X-Channel-ID',
  'X-Channel-Id'
];

const isAsteriskUniqueId = (value) => /^\d+\.\d+(?:\.\d+)?$/.test(String(value ?? '').trim());

export const getSipRequestHeader = (request, headerName) => {
  if (!request || !headerName) return '';

  if (typeof request.getHeaders === 'function') {
    try {
      const headerValue = normalizeSipHeaderValue(request.getHeaders(headerName));
      if (headerValue) return headerValue;
    } catch {
      // Fall through to getHeader/raw storage.
    }
  }

  if (typeof request.getHeader === 'function') {
    try {
      const headerValue = normalizeSipHeaderValue(request.getHeader(headerName));
      if (headerValue) return headerValue;
    } catch {
      // Fall through to the raw JsSIP header collection below.
    }
  }

  const headers = request.headers;
  if (!headers || typeof headers !== 'object') return '';

  const normalizedName = String(headerName).toLowerCase();
  const matchingName = Object.keys(headers).find(
    (name) => String(name).toLowerCase() === normalizedName
  );

  return matchingName ? normalizeSipHeaderValue(headers[matchingName]) : '';
};

const getFirstSipRequestHeader = (request, headerNames) =>
  firstNonEmpty(...headerNames.map((headerName) => getSipRequestHeader(request, headerName)));

const normalizeCallerDigits = (value) => String(value ?? '').replace(/\D/g, '');

const areCallerNumbersMatching = (left, right) => {
  const leftDigits = normalizeCallerDigits(left);
  const rightDigits = normalizeCallerDigits(right);
  if (!leftDigits || !rightDigits) return false;

  return (
    leftDigits === rightDigits ||
    leftDigits.endsWith(rightDigits) ||
    rightDigits.endsWith(leftDigits)
  );
};

const getCallerNumberFromSource = (source = {}) =>
  firstNonEmpty(
    source.callerNumber,
    source.CallerNumber,
    source.cli,
    source.CLI,
    source.number,
    source.calledNumber,
    source.CalledNumber,
    source.PhoneNo,
    source.phoneNo
  );

const resolveStrictCallUniqueId = (source = {}) =>
  firstNonEmpty(
    source.callId,
    source.CallId,
    source.callStateUniqueId,
    source.CallStateUniqueId,
    source.callUniqueId,
    source.CallUniqueId,
    source.uniqueId,
    source.UniqueId,
    source.ivrUid,
    source.IVR_UID,
    source.webCallId,
    source.linkedId,
    source.LinkedId
  );

const getSourceChannelId = (source = {}) =>
  firstNonEmpty(source.channelId, source.ChannelId, source.callStateChannelId, source.CallStateChannelId);

export const isSipNotificationChannelId = (value) =>
  String(value ?? '').trim().toLowerCase().startsWith('sip:');

export const resolveCallControlChannelId = (source = {}) => {
  const explicitChannelId = firstNonEmpty(
    source.callStateChannelId,
    source.CallStateChannelId,
    source.ariChannelId,
    source.AriChannelId
  );
  if (explicitChannelId && !isSipNotificationChannelId(explicitChannelId)) {
    return explicitChannelId;
  }

  const channelId = firstNonEmpty(source.channelId, source.ChannelId);
  return isSipNotificationChannelId(channelId) ? '' : channelId;
};

export const enrichIncomingCallIdentifiers = (call, identitySources = []) => {
  if (!call) return call;

  const sources = Array.isArray(identitySources) ? identitySources.filter(Boolean) : [];
  const callChannelId = getSourceChannelId(call);
  const callCallerNumber = getCallerNumberFromSource(call);
  const matchingSource = sources.find((source) => {
    const uniqueId = resolveStrictCallUniqueId(source);
    if (!uniqueId) return false;

    const sourceChannelId = getSourceChannelId(source);
    if (callChannelId && sourceChannelId && callChannelId === sourceChannelId) {
      return true;
    }

    const sourceCallerNumber = getCallerNumberFromSource(source);
    return areCallerNumbersMatching(callCallerNumber, sourceCallerNumber);
  });

  if (!matchingSource) return call;

  const callUniqueId = resolveStrictCallUniqueId(matchingSource || {});
  const controlChannelId =
    resolveCallControlChannelId(call) ||
    resolveCallControlChannelId(matchingSource || {});
  if (!callUniqueId && !controlChannelId) return call;

  return {
    ...call,
    callId: call.callId || callUniqueId || '',
    uniqueId: call.uniqueId || callUniqueId || '',
    webCallId: call.webCallId || callUniqueId || '',
    ivrUid: call.ivrUid || matchingSource?.ivrUid || matchingSource?.IVR_UID || '',
    linkedId: call.linkedId || matchingSource?.linkedId || matchingSource?.LinkedId || '',
    stableId: call.stableId || callUniqueId || '',
    callStateChannelId: call.callStateChannelId || controlChannelId,
    ariChannelId: call.ariChannelId || controlChannelId
  };
};

const sipReceivedAtBySession = new WeakMap();

const getSipReceivedAt = (session) => {
  if (sipReceivedAtBySession.has(session)) {
    return sipReceivedAtBySession.get(session);
  }

  const receivedAt = Date.now();
  sipReceivedAtBySession.set(session, receivedAt);
  return receivedAt;
};

export const buildSipIncomingCallNotification = (session) => {
  if (!session) return null;

  const remoteIdentity = session.remote_identity || session._remote_identity || {};
  const request = session.request || session._request || {};
  const requestFrom = request.from || {};
  const callerNumber = firstNonEmpty(
    remoteIdentity?.uri?.user,
    requestFrom?.uri?.user
  );
  const webCallId = firstNonEmpty(
    session.data?.webCallId,
    session.data?.callId,
    session.data?.uniqueId,
    session.data?.asteriskUniqueId,
    getFirstSipRequestHeader(request, ASTERISK_UNIQUE_ID_HEADERS)
  );
  const sipRequestCallId = firstNonEmpty(
    request.call_id,
    request.callId,
    getSipRequestHeader(request, 'Call-ID')
  );
  const callStateChannelId = getFirstSipRequestHeader(
    request,
    ASTERISK_CONTROL_CHANNEL_HEADERS
  );
  const displayCallId = firstNonEmpty(
    webCallId,
    isAsteriskUniqueId(sipRequestCallId) ? sipRequestCallId : ''
  );
  const sessionId = firstNonEmpty(displayCallId, sipRequestCallId, session.id, callerNumber, 'incoming');

  return {
    channelId: `sip:${sessionId}`,
    callId: displayCallId,
    callerName: firstNonEmpty(
      remoteIdentity.display_name,
      requestFrom.display_name,
      callerNumber,
      'Unknown Caller'
    ),
    callerNumber,
    stableId: sessionId,
    uniqueId: displayCallId,
    webCallId: displayCallId,
    sipCallId: sipRequestCallId,
    callStateChannelId,
    ariChannelId: callStateChannelId,
    linkedId: '',
    ivrUid: '',
    duration: 0,
    receivedAt: getSipReceivedAt(session),
    source: 'sip'
  };
};

export const getIncomingNotificationCalls = (ariCalls, sipSession, identitySources = []) => {
  const availableAriCalls = Array.isArray(ariCalls) ? ariCalls.filter(Boolean) : [];
  if (availableAriCalls.length) {
    return availableAriCalls.map((call) => enrichIncomingCallIdentifiers(call, identitySources));
  }

  const sipCall = buildSipIncomingCallNotification(sipSession);
  return sipCall ? [enrichIncomingCallIdentifiers(sipCall, identitySources)] : [];
};

export const getRingingDuration = (call, now = Date.now()) => {
  const baseDuration = Math.max(0, Math.floor(Number(call?.duration) || 0));
  if (call?.source !== 'sip') {
    return baseDuration;
  }

  const receivedAt = Number(call?.receivedAt);
  const currentTime = Number(now);
  if (!Number.isFinite(receivedAt) || receivedAt <= 0 || !Number.isFinite(currentTime)) {
    return baseDuration;
  }

  return Math.max(baseDuration, Math.floor(Math.max(0, currentTime - receivedAt) / 1000));
};

export const resolveIncomingCallDisplayId = (call = {}) => {
  const source = call || {};

  return firstNonEmpty(
    source.callId,
    source.CallId,
    source.callStateUniqueId,
    source.CallStateUniqueId,
    source.uniqueId,
    source.UniqueId,
    source.ivrUid,
    source.IVR_UID,
    source.webCallId,
    source.linkedId,
    source.LinkedId,
    source.source === 'sip' ? '' : source.stableId,
    source.source === 'sip' ? '' : source.channelId
  );
};

export const resolveCallHeaderId = (queryId, hasActiveCall, ...callIds) => {
  const resolvedQueryId = firstNonEmpty(queryId);
  if (resolvedQueryId || !hasActiveCall) {
    return resolvedQueryId;
  }

  return firstNonEmpty(...callIds);
};
